'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import ViralScoreMeter from '@/app/home/components/ViralScoreMeter';
import TrendsPanel from '@/app/home/components/TrendsPanel';

interface Generation {
  id: string;
  topic: string;
  platform: string;
  status: 'draft' | 'processing' | 'posted' | 'ready';
  viral_score: number;
  created_at: string;
  script?: string;
}

const MOCK_GENERATIONS: Generation[] = [
  { id: '1', topic: 'fat loss hack 2026', platform: 'TikTok', status: 'posted', viral_score: 87, created_at: '2026-05-15T14:22:00Z', script: 'You\'re losing fat the wrong way...' },
  { id: '2', topic: 'AI side hustle secret', platform: 'Instagram', status: 'posted', viral_score: 74, created_at: '2026-05-15T11:10:00Z', script: 'Nobody talks about this AI income stream...' },
  { id: '3', topic: 'morning routine hack', platform: 'YouTube Shorts', status: 'processing', viral_score: 61, created_at: '2026-05-14T20:05:00Z' },
  { id: '4', topic: 'passive income 2026', platform: 'TikTok', status: 'draft', viral_score: 55, created_at: '2026-05-14T16:30:00Z' },
  { id: '5', topic: 'gut health reset', platform: 'Instagram', status: 'posted', viral_score: 92, created_at: '2026-05-13T09:45:00Z', script: 'Stop eating these 3 foods immediately...' },
];

const PLATFORM_COLORS: Record<string, string> = {
  TikTok: '#C2185B',
  Instagram: '#ff7043',
  'YouTube Shorts': '#ffa726',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  posted: { label: 'Posted', color: '#4ade80', bg: 'rgba(74,222,128,0.1)' },
  processing: { label: 'Processing', color: '#fbbf24', bg: 'rgba(251,191,36,0.1)' },
  draft: { label: 'Draft', color: '#8A8A96', bg: 'rgba(138,138,150,0.1)' },
  ready: { label: 'Ready', color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
};

function StatCard({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-2"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--chrome-dim)' }}>
        {label}
      </span>
      <span
        className="text-3xl font-bold font-mono leading-none"
        style={{ color: accent ?? 'var(--chrome)' }}
      >
        {value}
      </span>
      {sub && (
        <span className="text-xs" style={{ color: 'var(--chrome-dim)' }}>
          {sub}
        </span>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const [generations, setGenerations] = useState<Generation[]>(MOCK_GENERATIONS);
  const [overallScore, setOverallScore] = useState(0);
  const [credits, setCredits] = useState(340);
  const [selectedTrend, setSelectedTrend] = useState<string | null>(null);

  useEffect(() => {
    // Animate viral score on mount
    const avg = Math.round(
      generations.reduce((sum, g) => sum + g.viral_score, 0) / generations.length
    );
    let current = 0;
    const step = Math.ceil(avg / 40);
    const timer = setInterval(() => {
      current = Math.min(current + step, avg);
      setOverallScore(current);
      if (current >= avg) clearInterval(timer);
    }, 30);
    return () => clearInterval(timer);
  }, []);

  const postedCount = generations.filter((g) => g.status === 'posted').length;
  const avgScore = Math.round(generations.reduce((s, g) => s + g.viral_score, 0) / generations.length);

  return (
    <main style={{ background: 'var(--blackout)', minHeight: '100vh' }}>
      <Header />

      <div className="max-w-7xl mx-auto px-6 pt-28 pb-20">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-bold font-display" style={{ color: 'var(--chrome)' }}>
              Dashboard
            </h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--chrome-dim)' }}>
              Your viral content command center
            </p>
          </div>
          <Link
            href="/home"
            className="btn-ruby px-5 py-2.5 rounded-lg text-sm font-semibold"
          >
            <span>⚡ Generate Content</span>
          </Link>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-12 gap-4">

          {/* Credits Card */}
          <div className="col-span-12 md:col-span-3">
            <StatCard
              label="Credits Remaining"
              value={credits}
              sub="Creator Plan · resets Jun 1"
              accent="var(--ruby)"
            />
          </div>

          {/* Posts This Month */}
          <div className="col-span-6 md:col-span-3">
            <StatCard
              label="Posts This Month"
              value={postedCount}
              sub={`of 500 allowed`}
              accent="#ffa726"
            />
          </div>

          {/* Avg Viral Score */}
          <div className="col-span-6 md:col-span-3">
            <StatCard
              label="Avg Viral Score"
              value={avgScore}
              sub="across all generations"
              accent="#4ade80"
            />
          </div>

          {/* Plan Badge */}
          <div className="col-span-12 md:col-span-3">
            <div
              className="rounded-2xl p-5 flex flex-col gap-2 h-full"
              style={{ background: 'rgba(194,24,91,0.08)', border: '1px solid rgba(194,24,91,0.25)' }}
            >
              <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--chrome-dim)' }}>
                Current Plan
              </span>
              <span className="text-2xl font-bold" style={{ color: 'var(--ruby)' }}>Creator</span>
              <span className="text-xs" style={{ color: 'var(--chrome-dim)' }}>$19/month · auto-renews</span>
              <button
                className="mt-auto text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
                style={{ background: 'rgba(194,24,91,0.15)', color: 'var(--ruby)', border: '1px solid rgba(194,24,91,0.3)' }}
              >
                Upgrade to Agency →
              </button>
            </div>
          </div>

          {/* Viral Score Meter — large */}
          <div
            className="col-span-12 md:col-span-4 rounded-2xl p-6 flex flex-col items-center justify-center gap-4"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(194,24,91,0.15)' }}
          >
            <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--chrome-dim)' }}>
              Overall Viral Score
            </span>
            <ViralScoreMeter score={overallScore} size="lg" showLabel />
            <p className="text-xs text-center max-w-[180px]" style={{ color: 'var(--chrome-dim)' }}>
              Based on your last {generations.length} generations
            </p>
            <div className="w-full grid grid-cols-3 gap-2 mt-2">
              {[
                { label: 'Hook', val: 82 },
                { label: 'Retention', val: 71 },
                { label: 'Emotion', val: 68 },
              ].map(({ label, val }) => (
                <div key={label} className="flex flex-col items-center gap-1">
                  <span className="text-xs" style={{ color: 'var(--chrome-dim)' }}>{label}</span>
                  <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${val}%`, background: 'var(--ruby)', transition: 'width 1s ease' }}
                    />
                  </div>
                  <span className="text-xs font-mono font-bold" style={{ color: 'var(--chrome)' }}>{val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Live Trend Suggestions */}
          <div className="col-span-12 md:col-span-8">
            {selectedTrend && (
              <div
                className="mb-3 px-4 py-2.5 rounded-xl flex items-center justify-between"
                style={{ background: 'rgba(194,24,91,0.1)', border: '1px solid rgba(194,24,91,0.25)' }}
              >
                <span className="text-sm" style={{ color: 'var(--chrome)' }}>
                  Selected trend: <strong style={{ color: 'var(--ruby)' }}>{selectedTrend}</strong>
                </span>
                <Link
                  href="/home"
                  className="text-xs font-semibold px-3 py-1 rounded-lg btn-ruby"
                >
                  <span>Generate →</span>
                </Link>
              </div>
            )}
            <TrendsPanel onSelectTrend={setSelectedTrend} />
          </div>

          {/* Generation History */}
          <div className="col-span-12">
            <div
              className="rounded-2xl p-5"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-semibold" style={{ color: 'var(--chrome)' }}>
                  Generation History
                </h2>
                <span className="text-xs" style={{ color: 'var(--chrome-dim)' }}>
                  {generations.length} total
                </span>
              </div>

              <div className="space-y-2">
                {generations.map((gen) => {
                  const status = STATUS_CONFIG[gen.status] ?? STATUS_CONFIG.draft;
                  const platformColor = PLATFORM_COLORS[gen.platform] ?? 'var(--chrome-dim)';
                  const date = new Date(gen.created_at);
                  const dateStr = `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;

                  return (
                    <div
                      key={gen.id}
                      className="flex items-center gap-4 p-4 rounded-xl transition-all"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                        e.currentTarget.style.borderColor = 'rgba(194,24,91,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                      }}
                    >
                      {/* Viral Score */}
                      <div className="flex-shrink-0">
                        <ViralScoreMeter score={gen.viral_score} size="sm" showLabel={false} />
                      </div>

                      {/* Topic & Script */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: 'var(--chrome)' }}>
                          {gen.topic}
                        </p>
                        {gen.script && (
                          <p className="text-xs truncate mt-0.5" style={{ color: 'var(--chrome-dim)' }}>
                            "{gen.script}"
                          </p>
                        )}
                      </div>

                      {/* Platform */}
                      <span
                        className="hidden sm:block text-xs font-semibold px-2.5 py-1 rounded-lg flex-shrink-0"
                        style={{ background: `${platformColor}18`, color: platformColor, border: `1px solid ${platformColor}30` }}
                      >
                        {gen.platform}
                      </span>

                      {/* Status */}
                      <span
                        className="text-xs font-semibold px-2.5 py-1 rounded-lg flex-shrink-0"
                        style={{ background: status.bg, color: status.color }}
                      >
                        {status.label}
                      </span>

                      {/* Date */}
                      <span className="hidden md:block text-xs flex-shrink-0" style={{ color: 'var(--chrome-dim)' }}>
                        {dateStr}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Account Overview */}
          <div className="col-span-12 md:col-span-6">
            <div
              className="rounded-2xl p-5 h-full"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <h2 className="text-base font-semibold mb-5" style={{ color: 'var(--chrome)' }}>
                Account Overview
              </h2>
              <div className="space-y-4">
                {[
                  { label: 'Email', value: 'creator@viralforge.ai' },
                  { label: 'Plan', value: 'Creator · $19/month' },
                  { label: 'Member Since', value: 'Jan 2026' },
                  { label: 'Total Generations', value: '142' },
                  { label: 'Auto-Post', value: 'Enabled' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: 'var(--chrome-dim)' }}>{label}</span>
                    <span className="text-sm font-medium" style={{ color: 'var(--chrome)' }}>{value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  className="flex-1 text-sm font-semibold py-2.5 rounded-lg btn-chrome"
                >
                  Edit Profile
                </button>
                <button
                  className="flex-1 text-sm font-semibold py-2.5 rounded-lg btn-ruby"
                >
                  <span>Manage Billing</span>
                </button>
              </div>
            </div>
          </div>

          {/* Credits Usage */}
          <div className="col-span-12 md:col-span-6">
            <div
              className="rounded-2xl p-5 h-full"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <h2 className="text-base font-semibold mb-5" style={{ color: 'var(--chrome)' }}>
                Credits Usage
              </h2>
              <div className="space-y-4">
                {[
                  { label: 'Script Generation', used: 120, total: 200, color: 'var(--ruby)' },
                  { label: 'Video Generation', used: 45, total: 100, color: '#ff7043' },
                  { label: 'Auto-Scheduling', used: 35, total: 100, color: '#ffa726' },
                  { label: 'Trend Analysis', used: 60, total: 100, color: '#4ade80' },
                ].map(({ label, used, total, color }) => (
                  <div key={label}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs" style={{ color: 'var(--chrome-dim)' }}>{label}</span>
                      <span className="text-xs font-mono" style={{ color: 'var(--chrome)' }}>
                        {used}/{total}
                      </span>
                    </div>
                    <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${(used / total) * 100}%`, background: color, transition: 'width 1s ease' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div
                className="mt-5 p-3 rounded-xl flex items-center justify-between"
                style={{ background: 'rgba(194,24,91,0.08)', border: '1px solid rgba(194,24,91,0.2)' }}
              >
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--chrome)' }}>
                    {credits} credits remaining
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--chrome-dim)' }}>
                    Resets in 17 days
                  </p>
                </div>
                <button
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg btn-ruby"
                >
                  <span>Buy More</span>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </main>
  );
}
